import { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";

/**
 * SystemStep — one word of the "Pick. Paste. Profit." flow with a hover/tap
 * popover that explains what the stage actually means.
 *
 * - Desktop: hovering the verb opens the popover; leaving closes it.
 * - Mobile / touch: tap the verb to toggle. Tap elsewhere to dismiss.
 * - Keyboard: focusable + Enter / Space toggles. Escape closes.
 */
export function SystemStep({
  verb,
  sub,
  accent = false,
  info,
  align = "left", // "left" anchors popover to step's left edge; "right" to its right edge
  testId,
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close on click outside (mobile after tap-to-open)
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={wrapperRef}
      className="relative"
      data-testid={testId}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={`What ${verb.replace(/\.$/, "")} means`}
        className="group text-left cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/60 focus-visible:ring-offset-4 focus-visible:ring-offset-black"
        data-testid={`${testId}-button`}
      >
        <div
          className={`font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-none transition-all duration-300 ${
            accent
              ? "text-purple-400 drop-shadow-[0_0_30px_rgba(168,85,247,0.5)]"
              : "text-white"
          } group-hover:text-purple-300`}
        >
          {verb}
        </div>
        <div className="text-zinc-400 text-base md:text-lg tracking-wide mt-3 inline-flex items-center gap-2.5">
          {sub}
          <span
            aria-hidden="true"
            className="inline-flex items-center gap-1 bg-purple-500 text-black px-2 py-1 text-[10px] tracking-[0.2em] uppercase font-mono leading-none font-bold shadow-[0_0_14px_rgba(168,85,247,0.55)] group-hover:bg-purple-400 group-hover:shadow-[0_0_22px_rgba(168,85,247,0.85)] transition-all"
          >
            <Info size={10} strokeWidth={3} />
            info
          </span>
        </div>
      </button>

      {/* Popover */}
      <div
        role="tooltip"
        data-testid={`${testId}-popover`}
        className={`absolute top-full mt-4 w-[20rem] md:w-[24rem] bg-[#0a0a0a] border border-purple-500/40 p-5 md:p-6 z-50 shadow-[0_0_40px_rgba(168,85,247,0.25)] transition-all duration-200 ${
          align === "right" ? "right-0" : "left-0"
        } ${
          open
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-1 pointer-events-none"
        }`}
      >
        {/* Connector pip */}
        <span
          aria-hidden="true"
          className={`absolute -top-px h-2 w-2 bg-purple-500 ${
            align === "right" ? "right-6" : "left-6"
          }`}
        />
        <div className="text-purple-400 text-xs tracking-[0.3em] uppercase mb-3">
          {verb.replace(/\.$/, "")}
        </div>
        <p className="text-zinc-200 text-base leading-relaxed">{info}</p>
      </div>
    </div>
  );
}
